'use client'

/**
 * Mapa de los dientes de leche, para la guía `cuando-se-caen-…`.
 *
 * El padre marca los que ya han caído y ve qué toca. Se guarda en localStorage,
 * sin cuenta: son veinte dientes en unos seis años, así que el valor está en que
 * pueda volver — pedirle una cuenta antes de darle nada mataría eso.
 *
 * No se guarda ningún dato del niño, solo qué dientes están marcados.
 */

import { useSyncExternalStore } from 'react'
import { Check, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  TOOTH_CHART_STORAGE_KEY,
  TOOTH_GROUPS,
  chartProgress,
  formatAgeRange,
  parseStoredChart,
  serializeChart,
  toothIdsFor,
  type ToothGroup,
  type ToothId,
} from '@/lib/guides/tools/tooth-chart'
import { CallBridge } from './call-bridge'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

const COPY = {
  heading: 'El mapa de sus dientes',
  intro:
    'Marca los que ya se le han caído y te decimos qué toca ahora. Se guarda en este navegador, así que puedes volver con el siguiente.',
  progress: '{fallen} de {total} dientes',
  nextLabel: 'Lo siguiente, si sigue el orden habitual',
  complete:
    '¡Los veinte! Ya no queda ni un diente de leche: el Ratón Pérez cierra su expediente con vosotros.',
  reset: 'Empezar de cero',
  orderNote:
    'Que un diente se retrase unos meses respecto a la tabla no significa nada por sí solo. Lo que conviene comentar en la revisión dental es que el ORDEN se altere mucho.',
  ctaTitle: '¿Y el próximo? Que se lo celebre el Ratón Pérez',
  ctaBody:
    'La noche del siguiente diente, el Ratón Pérez puede llamarle por videollamada: le felicita por su valentía, le llama por su nombre y le recuerda que se cepille.',
  ctaAction: 'Preparar la llamada del Ratón Pérez',
}

const ANALYTICS_BASE = {
  character_id: 'raton-perez',
  guide_slug: 'cuando-se-caen-los-dientes-de-leche',
  locale: 'es',
}

function GroupRow({
  group,
  fallen,
  onToggle,
}: {
  group: ToothGroup
  fallen: Set<ToothId>
  onToggle: (id: ToothId) => void
}) {
  const ids = toothIdsFor(group)
  const groupFallen = ids.filter((id) => fallen.has(id)).length
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 py-3">
      <div className="min-w-0">
        <p className="text-base text-white">{group.label}</p>
        <p className="text-sm text-white/60">
          {formatAgeRange(group)} · {groupFallen}/{group.count}
        </p>
      </div>
      <div className="flex gap-2">
        {ids.map((id, index) => {
          const isFallen = fallen.has(id)
          return (
            <button
              key={id}
              type="button"
              onClick={() => onToggle(id)}
              aria-pressed={isFallen}
              aria-label={`${group.label}, diente ${index + 1}${isFallen ? ' (caído)' : ''}`}
              className={`flex size-9 items-center justify-center rounded-lg border text-sm transition ${
                isFallen
                  ? 'border-emerald-300/60 bg-emerald-400/25 text-emerald-100'
                  : 'border-white/25 text-white/40 hover:border-white/60'
              }`}
            >
              {isFallen ? <Check className="size-4" aria-hidden /> : index + 1}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const EMPTY_SET: Set<ToothId> = new Set()
const listeners = new Set<() => void>()
let cachedSnapshot: Set<ToothId> = EMPTY_SET

function subscribeToChart(callback: () => void) {
  listeners.add(callback)
  return () => { listeners.delete(callback) }
}

function getChartSnapshot(): Set<ToothId> {
  if (typeof window === 'undefined') return EMPTY_SET
  try {
    const raw = window.localStorage.getItem(TOOTH_CHART_STORAGE_KEY)
    const parsed = parseStoredChart(raw)
    if (cachedSnapshot.size === parsed.length) {
      let same = true
      for (const id of cachedSnapshot) {
        if (!parsed.includes(id)) { same = false; break }
      }
      if (same) return cachedSnapshot
    }
    cachedSnapshot = new Set(parsed)
    return cachedSnapshot
  } catch {
    return EMPTY_SET
  }
}

function getServerSnapshot(): Set<ToothId> {
  return EMPTY_SET
}

function saveChart(fallen: Set<ToothId>) {
  try {
    window.localStorage.setItem(TOOTH_CHART_STORAGE_KEY, serializeChart(fallen))
  } catch {
    /* sin persistencia, la herramienta sigue funcionando en esta sesión */
  }
  cachedSnapshot = fallen
  listeners.forEach((listener) => listener())
}



function ChartHeader() {
  return (
    <div>
      <h3 className="font-christmas text-xl font-bold text-white md:text-2xl">{COPY.heading}</h3>
      <p className="mt-2 text-base text-white/80">{COPY.intro}</p>
    </div>
  )
}

function ChartProgress({ progress }: { progress: ReturnType<typeof chartProgress> }) {
  return (
    <p className="text-2xl font-bold text-white" data-testid="tooth-chart-progress">
      {COPY.progress
        .replace('{fallen}', String(progress.fallen))
        .replace('{total}', String(progress.total))}
    </p>
  )
}

function ChartStatus({ progress }: { progress: ReturnType<typeof chartProgress> }) {
  if (progress.complete) {
    return (
      <p className="rounded-2xl border border-emerald-300/30 bg-emerald-400/10 p-4 text-base text-emerald-100">
        {COPY.complete}
      </p>
    )
  }
  return (
    <div className="rounded-2xl border border-white/15 bg-white/5 p-4">
      <p className="text-sm font-semibold uppercase tracking-wide text-white/60">
        {COPY.nextLabel}
      </p>
      <p className="mt-1 text-lg text-white">
        {progress.next?.label} · {progress.next ? formatAgeRange(progress.next) : ''}
      </p>
    </div>
  )
}

function ChartActions({ fallen, onReset }: { fallen: Set<ToothId>, onReset: () => void }) {
  if (fallen.size === 0) return null
  return (
    <>
      <Button
        type="button"
        onClick={onReset}
        className="rounded-full bg-white/15 px-5 py-4 text-sm font-semibold text-white hover:bg-white/25"
      >
        <RotateCcw className="size-4" aria-hidden />
        {COPY.reset}
      </Button>
      <CallBridge
        childName=""
        character="raton-perez"
        locale="es"
        title={COPY.ctaTitle}
        body={COPY.ctaBody}
        action={COPY.ctaAction}
        placement="tooth_chart"
        analyticsProperties={ANALYTICS_BASE}
      />
    </>
  )
}

export function ToothChart() {
  const fallen = useSyncExternalStore(subscribeToChart, getChartSnapshot, getServerSnapshot)

  function toggle(id: ToothId) {
    const next = new Set(fallen)
    const wasFallen = next.delete(id)
    if (!wasFallen) next.add(id)
    saveChart(next)
    captureProductEvent(PRODUCT_EVENTS.GUIDE_TOOTH_CHART_UPDATED, {
      ...ANALYTICS_BASE,
      fallen_count: next.size,
      action: wasFallen ? 'unmark' : 'mark',
    })
  }

  const progress = chartProgress(fallen)

  return (
    <div
      data-testid="tooth-chart"
      className="space-y-4 rounded-3xl border border-white/15 bg-white/5 p-6 md:p-8"
    >
      <ChartHeader />
      <ChartProgress progress={progress} />
      <div>
        {TOOTH_GROUPS.map((group) => (
          <GroupRow key={group.key} group={group} fallen={fallen} onToggle={toggle} />
        ))}
      </div>
      <ChartStatus progress={progress} />
      <p className="text-sm text-white/70">{COPY.orderNote}</p>
      <ChartActions fallen={fallen} onReset={() => saveChart(new Set())} />
    </div>
  )
}
